import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import fleetApi from '../../services/api.service';

/**
 * Création de destinataires en masse depuis le modèle Excel, en deux temps.
 *
 * Le dépôt rend un aperçu ligne à ligne sans rien créer (dry_run=1) ; la
 * confirmation redépose le même fichier (dry_run=0) : l'API ne garde rien entre
 * les deux, un destinataire créé entre-temps est simplement signalé.
 *
 * @returns {object} - { preview, result, isUploading, isConfirming, uploadFile, confirmImport, reset, downloadTemplate }
 */
export default function useImportRecipients() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const send = (selectedFile, dryRun) => {
    const body = new FormData();
    body.append('file', selectedFile);
    body.append('dry_run', dryRun ? '1' : '0');

    return fleetApi.post('/addresses/import', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  /**
   * Dépose un fichier et rend son aperçu. Aucun destinataire n'est créé.
   *
   * @param {File} selectedFile - Le classeur déposé
   * @returns {Promise<object|null>} - L'aperçu, ou null
   */
  const uploadFile = useCallback(async (selectedFile) => {
    setIsUploading(true);
    setResult(null);

    try {
      const { data } = await send(selectedFile, true);
      setFile(selectedFile);
      setPreview(data);

      return data;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Le fichier n'a pas pu être lu, vérifiez qu'il suit le modèle."
      );
      setFile(null);
      setPreview(null);

      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Confirme l'aperçu : les lignes prêtes deviennent des destinataires.
   *
   * @returns {Promise<object|null>} - Le compte rendu, ou null
   */
  const confirmImport = useCallback(async () => {
    if (!file) return null;
    setIsConfirming(true);

    try {
      const { data } = await send(file, false);
      setPreview(null);
      setResult(data);

      return data;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "L'import n'a pas abouti, veuillez réessayer."
      );

      return null;
    } finally {
      setIsConfirming(false);
    }
  }, [file]);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResult(null);
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      const response = await fleetApi.get('/addresses/import/template', {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.download = 'modele-import-destinataires.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Le modèle n'a pas pu être téléchargé.");
    }
  }, []);

  return {
    preview,
    result,
    isUploading,
    isConfirming,
    uploadFile,
    confirmImport,
    reset,
    downloadTemplate,
  };
}
